import { trainerRoleLabel } from './trainerUtils';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  RefreshCw, AlertCircle, Download, User, GraduationCap, BookOpen,
  Award, FileText,
} from 'lucide-react';
import ChartContainer from '../ChartContainer';
import DashboardTabHeader from '../shared/DashboardTabHeader';
import { InfoTip } from '../shared/FormKit';
import SearchSelect from '../shared/SearchSelect';
import StatTile from './StatTile';
import { QualificationList } from './TrainingLists';
import { exportSheetsToExcel } from '../../utils/excelExport';
import { formatDate } from '../../utils/dateFormat';
import { getDefaultDateRange, officialPeriodStart, officialPeriodEnd, officialPeriodRangeLabel, getLdSettings } from '../../utils/dateRange';
import { API_BASE_URL } from '../../utils/api';
import { useAuth } from '../../contexts/AuthContext';
import { computeTrainingStats, inPeriod } from '../../utils/trainingStats';
import { computeCategoryStats, monthRange, monthLabelOf } from '../../utils/categoryStats';
import { BAND_BY_KEY, bandOf, progressPct } from './complianceBands';
import { exportExcelBtnCls } from '../shared/ExportExcelButton';

// Staff Journey: one staff member's complete learning
// history — hour buckets against the target, a cumulative curve, their
// category mix, and a month-by-month timeline of everything that happened
// (sessions attended or taught, External CPD, qualifications, agreements,
// absences). Data comes whole from /api/ld/staff/:id/journey (all time) and
// the period is applied here with the same utils as every other tab, so this
// tab's buckets can never disagree with Training Hours or the compliance
// table for the same person and period.

/** One event on the timeline. Colours follow the dashboard's existing bucket
 *  hues (mandatory blue / external green / qualification amber …). */
const EVENT_TYPES = {
  session: { label: 'Internal session', icon: GraduationCap, circle: 'bg-blue-100 text-blue-700 border-blue-200' },
  taught: { label: 'Taught session', icon: User, circle: 'bg-purple-100 text-purple-700 border-purple-200' },
  cpd: { label: 'External CPD', icon: BookOpen, circle: 'bg-green-100 text-green-700 border-green-200' },
  qualification: { label: 'Qualification awarded', icon: Award, circle: 'bg-amber-100 text-amber-700 border-amber-200' },
  agreement: { label: 'Training agreement', icon: FileText, circle: 'bg-indigo-100 text-indigo-700 border-indigo-200' },
  absence: { label: 'Absence', icon: AlertCircle, circle: 'bg-gray-100 text-gray-500 border-gray-200' },
};

const round1 = (hours) => Number((hours || 0).toFixed(1));

/** 'YYYY-MM' of an ISO date string. */
const monthKeyOf = (dateStr) => String(dateStr || '').slice(0, 7);

/** '2025-10' → 'October 2025' for the timeline month headings. */
const MONTHS_FULL = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];
const monthHeading = (key) => {
  const [y, m] = key.split('-').map((v) => parseInt(v, 10));
  return MONTHS_FULL[m - 1] ? `${MONTHS_FULL[m - 1]} ${y}` : key;
};

const StaffJourney = ({ initialStaffId = null }) => {
  const { hasPermission } = useAuth();

  const [options, setOptions] = useState([]);
  const [optionsError, setOptionsError] = useState(null);
  const [staffId, setStaffId] = useState(initialStaffId ? String(initialStaffId) : '');
  const [journey, setJourney] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const [startDate, setStartDate] = useState(() => getDefaultDateRange().startDate);
  const [endDate, setEndDate] = useState(() => getDefaultDateRange().endDate);

  const target = getLdSettings().mandatoryHoursTarget;

  // Jumping here from the compliance table selects that person.
  useEffect(() => {
    if (initialStaffId) setStaffId(String(initialStaffId));
  }, [initialStaffId]);

  useEffect(() => {
    let alive = true;
    fetch(`${API_BASE_URL}/api/ld/staff/options`, { credentials: 'include' })
      .then((res) => (res.ok ? res.json() : Promise.reject(new Error('Failed to load staff list'))))
      .then((body) => { if (alive) setOptions(body.data || []); })
      .catch((e) => { if (alive) setOptionsError(e.message); });
    return () => { alive = false; };
  }, []);

  const fetchJourney = useCallback(async () => {
    if (!staffId) { setJourney(null); return; }
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API_BASE_URL}/api/ld/staff/${staffId}/journey`, { credentials: 'include' });
      const body = await res.json();
      if (!res.ok) throw new Error(body.error || 'Failed to load staff journey');
      setJourney(body);
      setLastUpdated(new Date());
    } catch (e) {
      setError(e.message);
      setJourney(null);
    } finally {
      setLoading(false);
    }
  }, [staffId]);

  useEffect(() => { fetchJourney(); }, [fetchJourney]);

  const pickerOptions = useMemo(() => options.map((o) => ({
    value: String(o.staffId),
    label: `${o.name} (${o.position || '—'})${o.status === 'Resigned' ? ' — Resigned' : ''}`,
  })), [options]);

  // ── Aggregations (all client-side, shared utils) ───────────────────────────

  const stats = useMemo(
    () => computeTrainingStats(journey?.attendance || [], journey?.cpdRecords || [], startDate, endDate),
    [journey, startDate, endDate]
  );
  const person = stats.peopleArray[0] || null;
  const mandatoryHours = person ? person.mandatoryHours : 0;
  const band = BAND_BY_KEY[bandOf(mandatoryHours, target)];
  const pct = progressPct(mandatoryHours, target);

  const absencesInPeriod = useMemo(
    () => (journey?.absences || []).filter((a) => inPeriod(a.date, startDate, endDate)),
    [journey, startDate, endDate]
  );

  const categoryStats = useMemo(
    () => computeCategoryStats(journey?.attendance || [], journey?.cpdRecords || [], startDate, endDate),
    [journey, startDate, endDate]
  );
  const categoryDonut = Object.fromEntries(
    categoryStats.categories
      .filter((c) => c.totalHours > 0)
      .map((c) => [c.category.length > 42 ? `${c.category.slice(0, 41)}…` : c.category, round1(c.totalHours)])
  );

  // Cumulative hours, month by month over the period (or over the months with
  // data when the bounds are cleared). Two curves + the target line: mandatory
  // is what is measured, total shows everything the person put in.
  const cumulative = useMemo(() => {
    if (!journey) return { labels: [], datasets: [] };
    const events = [
      ...stats.filteredAttendance.filter((r) => !r.isTrainer).map((r) => ({
        month: monthKeyOf(r.date),
        mandatory: r.isMandatory ? (r.trainingHours || 0) : 0,
        total: r.trainingHours || 0,
      })),
      ...stats.filteredCpdRecords.map((r) => ({
        month: monthKeyOf(r.certificateDate),
        mandatory: 0,
        total: r.hours || 0,
      })),
    ];
    const months = (startDate && endDate)
      ? monthRange(monthKeyOf(startDate), monthKeyOf(endDate))
      : [...new Set(events.map((e) => e.month))].sort();
    let mand = 0;
    let tot = 0;
    const mandSeries = [];
    const totSeries = [];
    months.forEach((mk) => {
      events.filter((e) => e.month === mk).forEach((e) => { mand += e.mandatory; tot += e.total; });
      mandSeries.push(round1(mand));
      totSeries.push(round1(tot));
    });
    return {
      labels: months.map(monthLabelOf),
      datasets: [
        {
          label: 'Internal Mandatory (cumulative)',
          data: mandSeries,
          borderColor: '#3B82F6',
          backgroundColor: 'transparent',
          borderWidth: 2,
          fill: false,
          tension: 0.3,
          pointRadius: 3,
        },
        {
          label: 'All buckets (cumulative)',
          data: totSeries,
          borderColor: '#1f2937',
          backgroundColor: 'transparent',
          borderWidth: 2,
          borderDash: [5, 5],
          fill: false,
          tension: 0.3,
          pointRadius: 3,
        },
        {
          label: `Target (${target}h)`,
          data: months.map(() => target),
          borderColor: '#b45309',
          backgroundColor: 'transparent',
          borderWidth: 1.5,
          borderDash: [3, 3],
          fill: false,
          pointRadius: 0,
        },
      ],
    };
  }, [journey, stats, startDate, endDate, target]);

  // ── Timeline ───────────────────────────────────────────────────────────────

  const timeline = useMemo(() => {
    if (!journey) return [];
    const events = [];
    stats.filteredAttendance.forEach((r) => {
      events.push({
        type: r.isTrainer ? 'taught' : 'session',
        date: r.date,
        title: r.courseTitle || r.sessionCode,
        hours: r.isTrainer ? r.trainerHours : r.trainingHours,
        category: r.categoryTitle,
        mandatory: !r.isTrainer && r.isMandatory,
        detail: [
          r.startEndTime,
          r.venueName,
          r.isTrainer ? trainerRoleLabel(r.trainerRole) : null,
          !r.isTrainer && r.trainerNames ? `Trainer: ${r.trainerNames}` : null,
        ].filter(Boolean).join(' · '),
      });
    });
    stats.filteredCpdRecords.forEach((r) => {
      events.push({
        type: 'cpd',
        date: r.certificateDate,
        title: r.trainingTitle,
        hours: r.hours,
        category: r.categoryTitle,
        detail: r.fileCount > 0 ? `${r.fileCount} certificate${r.fileCount === 1 ? '' : 's'} on file` : null,
      });
    });
    journey.qualifications
      .filter((q) => q.awardedDate && inPeriod(q.awardedDate, startDate, endDate))
      .forEach((q) => {
        events.push({
          type: 'qualification',
          date: q.awardedDate,
          title: q.title,
          detail: [q.fullName, q.expiryDate ? `expires ${formatDate(q.expiryDate)}` : null].filter(Boolean).join(' · '),
        });
      });
    journey.agreements
      .filter((a) => a.agreementDate && inPeriod(a.agreementDate, startDate, endDate))
      .forEach((a) => {
        events.push({
          type: 'agreement',
          date: a.agreementDate,
          title: a.professionalQualification || 'Training agreement',
          detail: [
            a.sponsorshipAwarded ? 'Sponsorship awarded' : 'No sponsorship',
            a.cappedAt != null ? `capped at $${Number(a.cappedAt).toLocaleString()}` : null,
          ].filter(Boolean).join(' · '),
        });
      });
    absencesInPeriod.forEach((a) => {
      events.push({
        type: 'absence',
        date: a.date,
        title: a.courseTitle || a.sessionCode,
        detail: [a.reasonCategory, a.reasonDetail].filter(Boolean).join(' — '),
      });
    });
    // Newest first; the reader starts from where the person is now.
    events.sort((a, b) => (a.date < b.date ? 1 : a.date > b.date ? -1 : 0));
    const byMonth = [];
    events.forEach((e) => {
      const mk = monthKeyOf(e.date);
      const last = byMonth[byMonth.length - 1];
      if (last && last.month === mk) last.events.push(e);
      else byMonth.push({ month: mk, events: [e] });
    });
    return byMonth;
  }, [journey, stats, absencesInPeriod, startDate, endDate]);

  const periodLabel = (startDate || endDate)
    ? `${startDate ? formatDate(startDate) : 'Beginning'} – ${endDate ? formatDate(endDate) : 'Present'}`
    : 'All time';

  const handleExport = () => {
    if (!journey) return;
    const staff = journey.staff;
    const summary = [
      { Metric: 'Staff', Value: `${staff.name} (${staff.email})` },
      { Metric: 'Designation', Value: staff.position || '' },
      { Metric: 'Department', Value: staff.department || '' },
      { Metric: 'Grade', Value: staff.grade || '' },
      { Metric: 'Status', Value: staff.status },
      { Metric: 'Period', Value: periodLabel },
      { Metric: 'Internal Mandatory Hours', Value: round1(mandatoryHours) },
      { Metric: 'Target', Value: target },
      { Metric: 'Internal Non-mandatory Hours', Value: round1(person ? person.nonMandatoryHours : 0) },
      { Metric: 'External CPD Hours', Value: round1(person ? person.externalHours : 0) },
      { Metric: 'Total Hours', Value: round1(person ? person.totalHours : 0) },
      { Metric: 'Sessions Attended', Value: person ? person.internalSessions : 0 },
      { Metric: 'Sessions Taught', Value: stats.trainer.totalSessions },
      { Metric: 'Absences', Value: absencesInPeriod.length },
      { Metric: 'Qualifications Held', Value: journey.qualifications.length },
    ];
    const attendanceSheet = stats.filteredAttendance.map((r) => ({
      'Date': r.date,
      'Course': r.courseTitle,
      'Session': r.sessionCode,
      'Category': r.categoryTitle || 'Uncategorised',
      'Mandatory': r.isMandatory ? 'Yes' : 'No',
      'Role': r.isTrainer ? trainerRoleLabel(r.trainerRole) : 'Participant',
      'Hours': round1(r.isTrainer ? r.trainerHours : r.trainingHours),
    }));
    const cpdSheet = stats.filteredCpdRecords.map((r) => ({
      'Certificate Date': r.certificateDate,
      'Training': r.trainingTitle,
      'Category': r.categoryTitle || 'Uncategorised',
      'Hours': round1(r.hours),
      'Certificates on file': r.fileCount,
    }));
    const qualSheet = journey.qualifications.map((q) => ({
      'Qualification': q.title,
      'Full Name': q.fullName || '',
      'Awarding Body': q.awardingBody || '',
      'Awarded': q.awardedDate || '',
      'Expiry': q.expiryDate || '',
      'Source': q.sourceRequestNo || q.source,
    }));
    const absenceSheet = absencesInPeriod.map((a) => ({
      'Session Date': a.date,
      'Course': a.courseTitle,
      'Session': a.sessionCode,
      'Reason': a.reasonCategory,
      'Detail': a.reasonDetail || '',
      'Evidence': a.evidence ? 'Yes' : 'No',
    }));
    exportSheetsToExcel([
      { name: 'Summary', rows: summary },
      { name: 'Internal Attendance', rows: attendanceSheet },
      { name: 'External CPD', rows: cpdSheet },
      { name: 'Qualifications', rows: qualSheet },
      { name: 'Absences', rows: absenceSheet },
    ], `Staff_Journey_${staff.name.replace(/[^A-Za-z0-9]+/g, '_')}.xlsx`);
  };

  const staff = journey?.staff || null;

  return (
    <div className="space-y-6">
      <DashboardTabHeader
        description="One staff member's complete learning history — hours against the target, their category mix, and a timeline of sessions, External CPD, qualifications and absences."
        lastUpdated={lastUpdated}
        actions={
          <>
            <button
              onClick={fetchJourney}
              disabled={loading || !staffId}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
            {hasPermission('ld:view') && (
              <button
                onClick={handleExport}
                disabled={!journey}
                className={exportExcelBtnCls}
                title="Summary, attendance, External CPD, qualifications and absences — as filtered"
              >
                <Download className="w-4 h-4" />
                Export Excel
              </button>
            )}
          </>
        }
      />

      {/* Staff picker + period */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Staff Member</label>
            <SearchSelect
              value={staffId}
              onChange={setStaffId}
              options={pickerOptions}
              placeholder="Search staff by name…"
            />
            {optionsError && <p className="mt-1 text-xs text-red-600">{optionsError}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Period
            </label>
            <div className="flex flex-wrap items-center gap-2">
              <input
                type="date"
                value={startDate}
                max={endDate || undefined}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <span className="text-sm text-gray-500">to</span>
              <input
                type="date"
                value={endDate}
                min={startDate || undefined}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <button
                onClick={() => { setStartDate(officialPeriodStart()); setEndDate(officialPeriodEnd()); }}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              >
                This Training Year ({officialPeriodRangeLabel()})
              </button>
              <button
                onClick={() => { setStartDate(''); setEndDate(''); }}
                className="px-3 py-2 text-sm text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              >
                All Time
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-800">
          <p className="text-sm">{error}</p>
        </div>
      )}

      {!staffId && !error && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-10 text-center">
          <p className="text-gray-600 font-medium">Pick a staff member to see their learning journey</p>
        </div>
      )}

      {loading && staffId && !journey && (
        <div className="flex items-center justify-center p-8">
          <div className="text-center">
            <RefreshCw className="w-8 h-8 animate-spin text-blue-600 mx-auto mb-2" />
            <p className="text-gray-600">Loading learning journey...</p>
          </div>
        </div>
      )}

      {journey && staff && (
        <>
          {/* Profile card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex flex-wrap items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-3">
                  <h3 className="text-xl font-semibold text-gray-900">{staff.name}</h3>
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                    staff.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'
                  }`}>{staff.status}</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">
                  {staff.position || '—'}{staff.grade ? ` · ${staff.grade}` : ''}{staff.department ? ` · ${staff.department}` : ''}
                </p>
                <p className="text-sm text-gray-600">{staff.email}</p>
                <p className="text-xs text-gray-600 mt-1">
                  {staff.dateJoined && <>Joined {formatDate(staff.dateJoined)}</>}
                  {staff.dateLeft && <> · Left {formatDate(staff.dateLeft)}</>}
                </p>
              </div>
              <div className="min-w-0 max-w-xl">
                <p className="text-xs uppercase tracking-wider text-gray-600 mb-2">Qualifications Held</p>
                {/* Same component My Records renders as a full list — one
                    definition of what a qualification says. */}
                <QualificationList records={journey.qualifications} variant="chips" />
                {journey.agreements.length > 0 && (
                  <p className="text-xs text-gray-500 mt-2">
                    {journey.agreements.filter((a) => a.sponsorshipAwarded).length} sponsored training agreement{journey.agreements.filter((a) => a.sponsorshipAwarded).length === 1 ? '' : 's'} on file
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* KPI row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Mandatory progress — the one measured figure, so it gets the big card */}
            <div className="border border-slate-200 rounded-lg px-4 py-3 bg-white md:col-span-2">
              <div className="flex items-center justify-between">
                <p className="text-xs text-slate-500">Internal Mandatory (of {target}h target)</p>
                <span className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full border ${band.badge}`}>
                  {band.short}
                </span>
              </div>
              <div className="flex items-center gap-3 mt-2">
                <span className={`text-2xl font-bold tabular-nums ${mandatoryHours >= target && target > 0 ? 'text-emerald-700' : 'text-slate-900'}`}>
                  {mandatoryHours.toFixed(1)}
                </span>
                <div className="flex-1 h-2.5 bg-gray-100 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${band.bar}`} style={{ width: `${pct}%` }} />
                </div>
                <span className="text-sm text-gray-500 tabular-nums w-12 text-right">{pct.toFixed(0)}%</span>
              </div>
              {target > mandatoryHours && (
                <p className="text-xs text-amber-700 mt-1">{(target - mandatoryHours).toFixed(1)} hrs still to go in this period</p>
              )}
            </div>
            <StatTile label="Internal Non-mandatory Hrs" value={(person ? person.nonMandatoryHours : 0).toFixed(1)} />
            <StatTile label="External CPD Hrs" value={(person ? person.externalHours : 0).toFixed(1)} tone="green" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <StatTile label="Total Hours" value={(person ? person.totalHours : 0).toFixed(1)} />
            <StatTile label="Sessions Attended" value={person ? person.internalSessions : 0} />
            <StatTile label="Sessions Taught" value={stats.trainer.totalSessions} />
            <StatTile label="External CPD Records" value={person ? person.externalSessions : 0} />
            <StatTile label="Absences" value={absencesInPeriod.length} tone={absencesInPeriod.length > 0 ? 'amber' : 'slate'} />
          </div>

          {/* Cumulative curve + category mix */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <ChartContainer
              title="Cumulative Hours over the Period"
              subtitle={`${periodLabel} • mandatory vs all buckets, against the ${target}h target`}
              data={cumulative}
              type="line"
              multiDataset
              height={320}
            />
            <ChartContainer
              title="Hours by Category"
              subtitle="Internal + External CPD combined — display only, never part of the target"
              data={categoryDonut}
              type="doughnut"
              height={320}
            />
          </div>

          {/* Timeline */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex flex-wrap items-baseline justify-between gap-2 mb-4">
              <h3 className="text-lg font-semibold text-gray-900 inline-flex items-center gap-1.5">
                Learning Timeline
                <InfoTip text="Absences shown here are excused-absence records; voided ones are excluded." />
              </h3>
              <p className="text-sm text-gray-500">{periodLabel}</p>
            </div>
            {timeline.length === 0 ? (
              <p className="text-sm text-gray-500 py-6 text-center">Nothing recorded in this period — try “All Time”.</p>
            ) : (
              <div className="space-y-6">
                {timeline.map((group) => (
                  <div key={group.month}>
                    <h4 className="text-sm font-semibold text-gray-700 sticky top-0 bg-white py-1">
                      {monthHeading(group.month)}
                    </h4>
                    <ul className="mt-2 space-y-3 border-l-2 border-gray-100 pl-5 ml-2">
                      {group.events.map((e, idx) => {
                        const t = EVENT_TYPES[e.type];
                        const Icon = t.icon;
                        return (
                          <li key={`${e.type}-${e.date}-${idx}`} className="relative">
                            <span className={`absolute -left-[33px] top-0.5 inline-flex items-center justify-center w-6 h-6 rounded-full border ${t.circle}`}>
                              <Icon className="w-3.5 h-3.5" />
                            </span>
                            <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
                              <div className="min-w-0">
                                <p className="text-sm text-gray-900 break-words">
                                  <span className="text-gray-600 mr-2 whitespace-nowrap">{formatDate(e.date)}</span>
                                  <span className="font-medium">{e.title}</span>
                                </p>
                                <p className="text-xs text-gray-500 mt-0.5">
                                  {t.label}
                                  {e.mandatory && (
                                    <span className="ml-2 inline-flex px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-800 text-[10px] font-semibold">Mandatory</span>
                                  )}
                                  {e.category && <span className="ml-2 text-purple-600">{e.category}</span>}
                                  {e.detail && <span className="ml-2">{e.detail}</span>}
                                </p>
                              </div>
                              {e.hours != null && e.hours > 0 && (
                                <span className="shrink-0 text-sm font-semibold text-gray-900 tabular-nums">
                                  {Number(e.hours).toFixed(1)} <span className="text-xs font-normal text-gray-600">hrs</span>
                                </span>
                              )}
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                ))}
              </div>
            )}
            <p className="text-xs text-gray-500 mt-4">
              Sessions are dated by session date, External CPD by certificate date, qualifications by awarded date.
            </p>
          </div>
        </>
      )}
    </div>
  );
};

export default StaffJourney;
